import type { JSX } from "preact";
import { useState } from "preact/hooks";

import { api } from "../api";
import { Button, Card, Empty, ErrorNote, Loading, Pill } from "../components/Ui";
import { ago, datetime } from "../format";
import { useApi } from "../useApi";

/**
 * Értesítések.
 *
 * A munkatársak belépéskor itt nézik meg, mi történt. Legfelül a legutóbb
 * jelentkező ügy, félkövérrel, amit az adott munkatárs még nem nyitott meg.
 *
 * **Ügy, nem esemény.** Egy 15 percenként ismétlődő hiba egyetlen sor
 * számlálóval — különben a lista zaj lenne, és épp az új hiba veszne el.
 */

export interface NotificationItem {
  id: number;
  level: "hiba" | "figyelmeztetes" | "info";
  category: string;
  title: string;
  body: string;
  source: string;
  occurrences: number;
  first_seen_at: string;
  last_seen_at: string;
  resolved_at: string | null;
  unread: boolean;
}

interface NotificationList {
  unread: number;
  items: NotificationItem[];
}

export interface UnreadCount {
  unread: number;
  errors: number;
}

type Filter = "mind" | "olvasatlan" | "hibak";

const FILTERS: { id: Filter; label: string }[] = [
  { id: "mind", label: "Mind" },
  { id: "olvasatlan", label: "Olvasatlan" },
  { id: "hibak", label: "Hibák" },
];

const LEVEL: Record<string, { tone: string; label: string }> = {
  hiba: { tone: "bad", label: "hiba" },
  figyelmeztetes: { tone: "warn", label: "figyelmeztetés" },
  info: { tone: "info", label: "tájékoztatás" },
};

export const CATEGORY_LABEL: Record<string, string> = {
  arlista: "Árlista",
  tudasbazis: "Tudásbázis",
  gmail: "E-mail",
  rendszer: "Rendszer",
};

export function NotificationsView({
  onCountChange,
}: {
  onCountChange: (count: UnreadCount) => void;
}): JSX.Element {
  const [filter, setFilter] = useState<Filter>("mind");
  const { data, error, loading, reload } = useApi<NotificationList>(
    `/admin/v1/notifications?filter=${filter}`,
  );
  const [open, setOpen] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);

  const toggle = async (item: NotificationItem): Promise<void> => {
    setOpen(open === item.id ? null : item.id);
    if (!item.unread) return;
    try {
      onCountChange(await api.post<UnreadCount>(`/admin/v1/notifications/${item.id}/read`));
      reload();
    } catch {
      // Az olvasottság jelölése nem kritikus: a tartalom így is megnyílt.
    }
  };

  const readAll = async (): Promise<void> => {
    setBusy(true);
    try {
      onCountChange(await api.post<UnreadCount>("/admin/v1/notifications/read-all"));
      reload();
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card
      title="Értesítések"
      hint="Szinkronhibák, új árlista-verziók, rendszerkiesések. Legfelül a legújabb."
      actions={
        <>
          <div className="segmented">
            {FILTERS.map((f) => (
              <button
                key={f.id}
                type="button"
                className={filter === f.id ? "is-active" : ""}
                onClick={() => setFilter(f.id)}
              >
                {f.label}
              </button>
            ))}
          </div>
          <Button onClick={() => void readAll()} disabled={busy || !data?.unread}>
            Összes olvasottnak jelölése
          </Button>
        </>
      }
    >
      {error && <ErrorNote>{error}</ErrorNote>}
      {loading && !data && <Loading />}
      {data && data.items.length === 0 && (
        <Empty>
          {filter === "mind"
            ? "Még nincs értesítés. Ha egy szinkron hibára fut, vagy új árlista-verzió élesedik, itt jelenik meg."
            : "Nincs a szűrésnek megfelelő értesítés."}
        </Empty>
      )}

      {data && data.items.length > 0 && (
        <ul className="notifs">
          {data.items.map((item) => (
            <NotificationRow
              key={item.id}
              item={item}
              open={open === item.id}
              onToggle={() => void toggle(item)}
            />
          ))}
        </ul>
      )}
    </Card>
  );
}

function NotificationRow({
  item,
  open,
  onToggle,
}: {
  item: NotificationItem;
  open: boolean;
  onToggle: () => void;
}): JSX.Element {
  const level = LEVEL[item.level] ?? { tone: "muted", label: item.level };
  const classes = [
    "notif",
    item.unread ? "is-unread" : "",
    item.resolved_at ? "is-resolved" : "",
    open ? "is-open" : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <li className={classes}>
      <button
        type="button"
        className="notif__head"
        aria-expanded={open}
        onClick={onToggle}
      >
        <span className="notif__dot" aria-label={item.unread ? "olvasatlan" : undefined} />
        <span className="notif__main">
          <span className="notif__title">{item.title}</span>
          <span className="notif__meta">
            <Pill tone={level.tone}>{level.label}</Pill>
            <span>{CATEGORY_LABEL[item.category] ?? item.category}</span>
            {item.occurrences > 1 && <span>{item.occurrences}× előfordult</span>}
            {item.resolved_at && <Pill tone="ok">megoldódott</Pill>}
          </span>
        </span>
        <span className="notif__time" title={datetime(item.last_seen_at)}>
          {ago(item.last_seen_at)}
        </span>
      </button>

      {open && (
        <div className="notif__body">
          {item.body && <p className="notif__text">{item.body}</p>}
          <p className="muted notif__facts">
            Először: {datetime(item.first_seen_at)}
            {item.occurrences > 1 && <> · utoljára: {datetime(item.last_seen_at)}</>}
            {item.resolved_at && <> · megoldódott: {datetime(item.resolved_at)}</>}
          </p>
        </div>
      )}
    </li>
  );
}
